import { call } from 'redux-saga/effects';
import { formatPercentage } from './NumberFormatters';
import { isDateBetween, isDateAfter } from './DateService';
import { formatDateDefault } from './DateFormatters';
import { intersection, indexOf, round, sortBy } from 'lodash';
import { fetchDataRequest, postDataRequest } from 'services/ApiService';
import { ORGID_PATH_PARAM, ORG_SURVEY_ANSWERS_ENDPOINT, COMPARE_ORG_SURVEY_ANSWERS_ENDPOINT } from 'constants/api';

const TOP_BOTTOM_THRESHOLD = 5;

// {
//     "surveys": [
//         {
//             "id": "634d1475334f91eeedf1e03b20190525",
//             "name": "Happy survey time",
//             "surveyId": "dc00cc7175004a68df46359b20190618",
//             "endDate": "08/08/2020",
//             "startDate": "07/07/2018"
//         }
//     ],
//     "id": "634d1475334f91eeedf1e03b20190524",
//     "email": "cpsolutions28@gmail.com",
//     "name": "Super Happy Org ADMIN",
// }

// [
//     {
//         "id": "883r1475334f91eeedf1e03b20190999",
//         "orgid": "634d1475334f91eeedf1e03b20190524",
//         "surveyId": "dc00cc7175004a68df46359b20190618",
//         "questionAnswers": [
//             {
//                 "empId": "634d1475334f91eeedf1e03b20190525",
//                 "answers": [
//                     {"questionId": "mission_and_purpose_v1", "answer": 4},
//                     {"questionId": "agile_planning_v1", "answer": 7}
//                 ]
//             }
//         ]
//     }
// ]

/**
 * Extract the active surveys (check end and start date) from the org survey list
 * @param {*} surveys - org surveys array of all surveys
 */
export const getActiveSurveys = (surveys) => {
    if (surveys) {
        return surveys.filter((survey) => isDateBetween(formatDateDefault(survey.startDate), formatDateDefault(survey.endDate), new Date()));
    }
    return [];
};

/**
 * Extract the inactive surveys (current date > end date) from the org survey list - note dont check each employee entry
 * (ie. if all individual emp surveys have been completed which could constitute that the survey is finished)
 * as new employees may join, etc
 * @param {*} surveys - org surveys array of all surveys
 */
export const getInactiveSurveys = (surveys) => {
    if (surveys) {
        return surveys.filter((survey) => isDateAfter(new Date(), formatDateDefault(survey.endDate)));
    }
    return [];
};

/**
 * Extract the inactive surveys (check end and start date) from the org survey list and return the count
 * @param {*} surveys - org surveys array of all surveys
 */
export const getInactiveSurveysCount = (surveys) => {
    if (surveys) {
        const list = getInactiveSurveys(surveys);
        return list ? list.length : 0;
    }
    return 0;
};

/**
 * Extract the active surveys (check end and start date) from the org survey list and return the count
 * @param {*} surveys - org surveys array of all surveys
 */
export const getActiveSurveysCount = (surveys) => {
    if (surveys) {
        const list = surveys.filter((survey) => isDateBetween(survey.startDate, survey.endDate, new Date()));
        return list ? list.length : 0;
    }
    return 0;
};

/**
 * Get a survey from the active survey list
 * @param {Array} surveys - org surveys array of all surveys - contains start and end date
 * @param {String} surveyId - the survey id to retrieve
 */
export const getActiveSurveyById = (surveys, surveyId) => {
    if (surveys?.length > 0 && surveyId) {
        const activeSurveys = getActiveSurveys(surveys).filter((survey) => survey.surveyId === surveyId);
        if (activeSurveys?.length > 0) {
            return activeSurveys[0]; // should only be one
        }
    }
    return undefined;
};

/**
 * Get a survey from the survey list - get survey groups and then the questions
 * @param {Array} surveys - array of all surveys
 * @param {String} surveyId - the survey id to retrieve
 */
export const getSurveyById = (surveys, surveyId) => {
    if (surveys?.length > 0 && surveyId) {
        const survey = surveys.filter((survey) => survey.id === surveyId);
        if (survey?.length > 0) {
            return survey[0]; // should only be one
        }
    }
    return undefined;
};

/**
 * Calculate the percent complete for the whole organisation per survey
 * @param {Array} survey
 * @param {Array} surveyAnswers
 */
export const getOrgSurveyPercentComplete = (survey, surveyAnswers) => {
    if (survey && surveyAnswers) {
        return formatPercentage(100);
    }
    return 0;
};

/**
 * Calculate the percent complete for an employee for a given survey
 * @param {Array} survey
 * @param {Array} surveyAnswers
 */
export const getOrgSurveyPercentCompleteByEmployee = (surveyId, empId, surveyAnswers) => {
    if (surveyId && empId && surveyAnswers) {
        return formatPercentage(100);
    }
    return 0;
};

/**
 *
 * @param {*} survey
 * @param {*} surveyGroups
 * @param {*} surveyQuestions
 */
export const buildEmpSurveyAnswers = (survey, surveyGroups) => {
    let empSurveyAnswers = [];
    if (survey?.groups) {
        survey.groups.forEach((group) => {
            surveyGroups.forEach((surveyGroup) => {
                if (surveyGroup?.id && surveyGroup.id === group) {
                    if (surveyGroup?.questions) {
                        surveyGroup.questions.forEach((question) => {
                            // add the question/ answer to the response
                            const answer = { questionId: question, answer: 0 };
                            empSurveyAnswers.push(answer);
                        });
                    }
                }
            });
        });
    }
    return empSurveyAnswers;
};

export const createDefaultOrgSurveyAnswers = (id, orgid, surveyId) => {
    return { id, orgid, surveyId, questionAnswers: [] };
};

/**
 * extract the employee survey answers from the org survey answers object
 * @param {*} orgSurveyAnswers
 * @param {*} empId
 */
export const getEmpSurveyAnswers = (orgSurveyAnswers, empId) => {
    return orgSurveyAnswers?.questionAnswers?.filter((questionAnswer) => questionAnswer.empId === empId) || [];
};

export const empSurveySummaryStatistics = (empSurveyAnswers) => {
    let summaryStatistics = {};
    const initSummaryStatistics = {
        percentCompleted: 0,
        percentToComplete: 100,
        numberOfQuestions: 0,
        questionsCompleted: 0,
        questionsToComplete: 0,
        answerAverage: 0,
    };
    let totalQuestions = 0;
    let totalQuestionsAnswered = 0;
    let answerTotal = 0;
    if (empSurveyAnswers?.answers) {
        for (const empSurveyAnswer of empSurveyAnswers.answers) {
            totalQuestions++;
            if (empSurveyAnswer?.answer > 0) {
                totalQuestionsAnswered++;
                answerTotal += empSurveyAnswer.answer;
            }
        }
    }
    summaryStatistics = {
        ...initSummaryStatistics,
        ...{ percentCompleted: totalQuestions > 0 ? (totalQuestionsAnswered / totalQuestions) * 100 : 0 },
        ...{ percentToComplete: 100 - (totalQuestions > 0 ? (totalQuestionsAnswered / totalQuestions) * 100 : 0) },
        ...{ numberOfQuestions: totalQuestions },
        ...{ questionsCompleted: totalQuestionsAnswered },
        ...{ questionsToComplete: totalQuestions - totalQuestionsAnswered },
        ...{ answerAverage: totalQuestionsAnswered > 0 ? answerTotal / totalQuestionsAnswered : 0 },
    };
    return summaryStatistics;
};

/**
 * Checks if the survey questions have answers - if all are answered then the survey is complete
 * This allows the option to close the survey
 * NOTE - the user can still change the answers up until they close the survey
 */
export const surveyComplete = (empSurveyAnswers) => {
    let surveyCompleted = true;
    if (empSurveyAnswers?.answers?.length > 0) {
        for (const empSurveyAnswer of empSurveyAnswers.answers) {
            if (empSurveyAnswer?.answer === 0) {
                surveyCompleted = false;
                break;
            }
        }
    } else {
        return false;
    }
    return surveyCompleted;
};

export const surveyActive = (empSurveyAnswers) => {
    return empSurveyAnswers?.surveyClosedDate === null || typeof empSurveyAnswers?.surveyClosedDate === 'undefined';
};

export const initialiseOrgSurveysCompareList = (orgSurveysCompareList) => {
    return orgSurveysCompareList?.length > 0
        ? orgSurveysCompareList.map((orgSurvey) => {
              return {
                  ...orgSurvey,
                  selected: false,
              };
          })
        : [];
};

/**
 * Transform the emp survey answers into data required for other services - i.e. charting
 * @param {Array} orgSurveyAnswers - can be 1 or 2 entries
 */
export const extractOrgSurveyCompareData = (orgSurveyAnswers) => {
    const INIT_QUESTION = {
        question: '',
        data: [],
    };
    const INIT_DATA = {
        id: '',
        values: {},
        engagementData: {},
    };
    const INIT_VALUES = {
        totalValue: 0,
        answeredCount: 0,
    };

    // answers grouped 1-2, 3-4, 5-6-7, 8-9
    const INIT_ENGAGEMENT_DATA = {
        activelyDisengaged: 0, // answers of 1-2
        passive: 0, // answers of 3-4
        moderatelyEngaged: 0, // answers of 5-6-7
        highlyEngaged: 0, // answers of 8-9
    };
    // only work with 2 surveys (more than 1 may be selected)
    // TODO need to select the 2 most recent surveys in the list to compare as the list can be in any order
    let compareData = [];
    while (orgSurveyAnswers?.length > 2) {
        // only keep 2 entries (TODO should be the latest 2 if more than 2 are selected)
        orgSurveyAnswers.pop();
    }
    const commonQuestions = compareSurveysCommonQuestions(orgSurveyAnswers);
    orgSurveyAnswers?.length > 0
        ? orgSurveyAnswers.forEach((orgSurveyAnswer) => {
              if (orgSurveyAnswer?.questionAnswers?.length > 0) {
                  orgSurveyAnswer.questionAnswers.forEach((questionAnswer) => {
                      // TODO take all answers or only if the survey has been completed ??? to be answered
                      // i.e. emp may have completed some question without finalising the survey
                      if (questionAnswer?.answers?.length > 0) {
                          questionAnswer.answers.forEach((answer) => {
                              // first check if the question is in the common question list (between both surveys) before adding to the results
                              if (answer?.answer > 0 && indexOf(commonQuestions, answer.questionId) > -1) {
                                  const foundQuestionEntry = compareData.find((item) => item.question === answer.questionId);
                                  if (foundQuestionEntry === undefined) {
                                      // no entry found so initialise and add to the array
                                      const initValues = { ...INIT_VALUES, totalValue: answer.answer, answeredCount: 1 };
                                      const initEngagement = updateEngagementData({ ...INIT_ENGAGEMENT_DATA }, answer.answer);
                                      const initData = { ...INIT_DATA, id: orgSurveyAnswer.id, values: { ...initValues }, engagementData: { ...initEngagement } };
                                      const initQuestion = { ...INIT_QUESTION, question: answer.questionId, data: [initData] };
                                      compareData.push(initQuestion);
                                  } else {
                                      // found the question - need to check if the survey exists - if so add the value otherwise create a new entry
                                      const foundDataSurveyEntry = foundQuestionEntry.data.find((item) => item.id === orgSurveyAnswer.id);
                                      if (foundDataSurveyEntry === undefined) {
                                          const initValues = { ...INIT_VALUES, totalValue: answer.answer, answeredCount: 1 };
                                          const initEngagement = updateEngagementData({ ...INIT_ENGAGEMENT_DATA }, answer.answer);
                                          const initData = { ...INIT_DATA, id: orgSurveyAnswer.id, values: { ...initValues }, engagementData: { ...initEngagement } };
                                          foundQuestionEntry.data.push(initData);
                                      } else {
                                          // found the data survey entry so update the value
                                          const initEngagement = updateEngagementData({ ...foundDataSurveyEntry.engagementData }, answer.answer);
                                          foundDataSurveyEntry.engagementData = { ...initEngagement };
                                          foundDataSurveyEntry.values.totalValue = foundDataSurveyEntry.values.totalValue + answer.answer;
                                          foundDataSurveyEntry.values.answeredCount = foundDataSurveyEntry.values.answeredCount + 1;
                                      }
                                  }
                              }
                          });
                      }
                  });
              }
              // return {
              //     ...orgSurveyAnswer,
              //     selected: false
              // };
          })
        : [];
    // console.log(JSON.stringify(compareData));
    return compareData;
};

/**
 * compare 2 surveys and return the common question from both - uses the first as the
 * if only 1 survey is passed then use all the questions as the base for the compare list
 * @param {Array} orgSurveyAnswers - must be only 1 or 2 entries
 */
const compareSurveysCommonQuestions = (orgSurveyAnswers) => {
    if (orgSurveyAnswers?.length == 1) {
        const questionsBase = Array.from(orgSurveyAnswers[0]?.questionAnswers[0]?.answers, (answer) => answer.questionId);
        return questionsBase;
    }
    if (orgSurveyAnswers?.length == 2) {
        //console.log(Array.from([1, 2, 3], x => x + x));
        // expected output: Array [2, 4, 6]
        const questionsBase = Array.from(orgSurveyAnswers[0]?.questionAnswers[0]?.answers, (answer) => answer.questionId);
        const questionsCompare = Array.from(orgSurveyAnswers[1]?.questionAnswers[0]?.answers, (answer) => answer.questionId);
        return intersection(questionsBase, questionsCompare);
    }
    return [];
};

/**
 * increment the correct entry
 * @param {INIT_ENGAGEMENT_DATA} data
 * @param {*} answer
 */
const updateEngagementData = (data, answer) => {
    if (data) {
        if (answer === 1 || answer === 2) {
            data.activelyDisengaged = data.activelyDisengaged + 1;
        } else if (answer === 3 || answer === 4) {
            data.passive = data.passive + 1;
        } else if (answer === 5 || answer === 6 || answer === 7) {
            data.moderatelyEngaged = data.moderatelyEngaged + 1;
        } else {
            data.highlyEngaged = data.highlyEngaged + 1;
        }
    }
    return data;
};

/**
 * Transform the emp survey answers into data required for radar, bar charts
 * NOTE - the first entry contains the unique keys to construct the charts
 * @param {Array} orgSurveyAnswers
 */
export const extractOrgSurveyCommonChartData = (orgSurveyData) => {
    const surveyResponseData = [];
    const uniqueKeys = [];
    const compareData = extractOrgSurveyCompareData(orgSurveyData);
    if (compareData) {
        compareData.forEach((entry) => {
            // eslint-disable-next-line no-undef
            let transformedMap = new Map();
            transformedMap.set('question', entry.question);
            entry.data.forEach((dataEntry) => {
                const aveValue = getAverageValue(dataEntry.values);
                transformedMap.set(dataEntry.id, aveValue);
                // check if the id exisits in the unique
                if (!uniqueKeys.includes(dataEntry.id)) {
                    uniqueKeys.push(dataEntry.id);
                }
            });
            let transformedObject = {};
            transformedMap.forEach((v, k) => {
                transformedObject = { ...transformedObject, ...{ [`${k}`]: v } };
            });
            surveyResponseData.push(transformedObject);
        });
    }
    surveyResponseData.unshift(uniqueKeys);
    // console.log(JSON.stringify(surveyResponseData));
    return surveyResponseData;
};

export const extractOrgSurveyRadarData = (orgSurveyData) => {
    return extractOrgSurveyCommonChartData(orgSurveyData);
};

export const extractOrgSurveyDistributionsData = (orgSurveyData) => {
    return extractOrgSurveyCommonChartData(orgSurveyData);
};

/**
 * Extract the top responses from the survey answers based on a weighted algorithm
 * @param {Array} orgSurveyData - OrgSurveyAnswers list
 * @param {*} surveyId
 */
export const extractOrgSurveyTopResponseData = (orgSurveyData, surveyId) => {
    const surveyResponseData = extractOrgSurveyResponseData(orgSurveyData, surveyId);
    const sortedData = sortTopEngagementData(surveyResponseData).reverse();
    while (sortedData?.length > TOP_BOTTOM_THRESHOLD) {
        // only keep the top TOP_BOTTOM_THRESHOLD entries
        sortedData.pop();
    }
    // console.log(JSON.stringify(sortedData));
    return sortedData;
};

/**
 * Extract the bottom responses from the survey answers based on a weighted algorithm
 * @param {Array} orgSurveyData - OrgSurveyAnswers list
 * @param {*} surveyId
 */
export const extractOrgSurveyBottomResponseData = (orgSurveyData, surveyId) => {
    const surveyResponseData = extractOrgSurveyResponseData(orgSurveyData, surveyId);
    const sortedData = sortBottomEngagementData(surveyResponseData).reverse();
    while (sortedData?.length > TOP_BOTTOM_THRESHOLD) {
        // only keep the top TOP_BOTTOM_THRESHOLD entries
        sortedData.pop();
    }
    // console.log(JSON.stringify(sortedData));
    return sortedData;
};

export const extractOrgSurveyResponseData = (orgSurveyData, surveyId) => {
    const surveyResponseData = [];
    const compareData = extractOrgSurveyCompareData(orgSurveyData);
    if (compareData) {
        compareData.forEach((answer) => {
            const questionId = answer?.question;
            answer?.data.forEach((surveyResult) => {
                if (surveyResult?.id === surveyId) {
                    // push the object to the final list
                    surveyResponseData.push({ question: questionId, ...surveyResult });
                }
            });
        });
    }
    return surveyResponseData;
};

/**
 * extract org survey answers and produce an array of {orgId: number, surveyId: number, data: [{name: string, amt: number}] } objects
 * @param {*} orgSurveyData - OrgSurveyAnswers list
 */
export const extractOrgSurveySimpleChartData = (orgSurveyData) => {
    const simpleChartResponseData = [];
    if (orgSurveyData) {
        orgSurveyData.forEach((survey) => {
            const surveyChartData = [];
            const data = extractOrgSurveyResponseData(orgSurveyData, survey.id);
            data.forEach((question) => {
                // push the object to the final list
                const aveValue = getAverageValue(question.values);
                surveyChartData.push({ name: question?.question, amt: aveValue });
            });

            simpleChartResponseData.push({ orgId: survey.orgid, surveyId: survey.surveyId, data: surveyChartData });
        });
    }
    return simpleChartResponseData;
};

/**
 * Apply a weighting to each grouping in the engagementData to sort the results
 * @param {Array} data
 */
export const sortTopEngagementData = (data) => {
    let sortedData;
    if (data) {
        sortedData = sortBy(data, [
            function (o) {
                return o.engagementData.highlyEngaged * 8 + o.engagementData.moderatelyEngaged * 6 + o.engagementData.passive * 4 + o.engagementData.activelyDisengaged * 2;
            },
        ]);
        return sortedData;
    }
    return data;
};

/**
 * Apply a weighting to each grouping in the engagementData to sort the results
 * @param {Array} data
 */
export const sortBottomEngagementData = (data) => {
    let sortedData;
    if (data) {
        sortedData = sortBy(data, [
            function (o) {
                return o.engagementData.highlyEngaged * 2 + o.engagementData.moderatelyEngaged * 4 + o.engagementData.passive * 6 + o.engagementData.activelyDisengaged * 8;
            },
        ]);
        return sortedData;
    }
    return data;
};

export const prepareOrgSurveyAnswerSagaCalls = (orgSurveysCompareList, selectedOrg) => {
    const orgSurveyAnswerCalls =
        orgSurveysCompareList?.length > 0
            ? orgSurveysCompareList
                  .filter((orgSurvey) => orgSurvey?.selected)
                  .map((orgSurvey) => {
                      if (orgSurvey?.selected) {
                          return call(fetchDataRequest, ORG_SURVEY_ANSWERS_ENDPOINT, [ORGID_PATH_PARAM, 'orgSurveyId'], [selectedOrg?.id, orgSurvey?.id]) || {};
                      }
                  })
            : [];
    return orgSurveyAnswerCalls;
};

export const prepareOrgSurveyAnswerSagaCall = (orgSurveysCompareList, selectedOrgId) => {
    if (Array.isArray(orgSurveysCompareList)) {
        const surveys = orgSurveysCompareList.map((survey) => survey?.surveyId);
        const payload = {
            orgid: selectedOrgId,
            compareSurveyIds: surveys,
        };
        return call(postDataRequest, COMPARE_ORG_SURVEY_ANSWERS_ENDPOINT, payload, null, [ORGID_PATH_PARAM], [selectedOrgId]) || {};
    }
    return null;
};

export const getCompareOrgSurveyAnswers = async (orgSurveysCompareList, selectedOrgId) => {
    if (Array.isArray(orgSurveysCompareList)) {
        const surveys = orgSurveysCompareList.filter((survey) => survey?.selected).map((survey) => survey?.id);
        const payload = {
            orgid: selectedOrgId,
            compareSurveyIds: surveys,
        };
        const result = await postDataRequest(COMPARE_ORG_SURVEY_ANSWERS_ENDPOINT, payload, null, [ORGID_PATH_PARAM], [selectedOrgId]);
        return result || [];
    }
    return [];
};

/**
 *
 * @param {*} values object that contains {answeredCount: number, totalvalue: number}
 */
const getAverageValue = (values) => {
    return values?.answeredCount > 0 ? round(values.totalValue / values.answeredCount, 1) : 0;
};



// WEBPACK FOOTER //
// ./services/SurveyService.js